import React from 'react';
import Icon from '@xo-union/tk-component-icons';
import { RouterContext } from '../../gs-component-right-rail';
import { Event, Household, GuestWeddingQuestions } from '../interfaces';
import { isKeyEnterSpace } from '../helpers';
import InvitationAnswers from './InvitationAnswers';
import QuestionsAndAnswers from './QuestionsAndAnswers';
import styles from './styles.scss';
import ComponentStateContext from '../ComponentStateContext';
import ZeroInvitationImage from './ZeroInvitationImage';

interface GRProps {
  events: Event[];
  household: Household;
  generalQuestions: GuestWeddingQuestions;
}

interface IconProps {
  onClick: () => void;
}

const EditIcon: React.FC<IconProps> = ({ onClick }) => (
  <div
    data-testid="EditIcon"
    role="button"
    className={styles.editIcon}
    onClick={onClick}
    tabIndex={0}
    onKeyDown={(e): void => {
      if (isKeyEnterSpace(e)) onClick();
    }}
  >
    <Icon name="edit" size="md" />
  </div>
);

const ZeroState: React.FC<{}> = () => (
  <div className={styles.zeroState}>
    <ZeroInvitationImage />
    <div>This party&apos;s still waiting to be invited!</div>
    <br />
    <div>Mark them as invited on your Guest List, then check back here to see and edit their responses.</div>
  </div>
);

const GuestResponses: React.FC<GRProps> = ({ events, household, generalQuestions: questions }) => {
  const { push } = React.useContext(RouterContext);
  const { setEditForm } = React.useContext(ComponentStateContext);
  const goToEdit = React.useCallback((): void => push('edit_response'), [push]);

  const renderGeneral = React.useCallback(() => {
    if (!questions || !questions?.length) return null;
    const { answers: householdAnswers = null } = household;
    return (
      <div className={styles.responseSection}>
        <h4 className={styles.sectionName}>
          General Questions
          <EditIcon
            onClick={(): void => {
              goToEdit();
              setEditForm(() => ({ type: 'GENERAL' }));
            }}
          />
        </h4>
        <QuestionsAndAnswers questions={questions} answers={householdAnswers} />
      </div>
    );
  }, [goToEdit, household, questions, setEditForm]);

  const renderEvents = React.useCallback(() => {
    return events?.map(e => {
      const hasEventInvitation = household?.people?.some(p => p?.invitations?.some(i => i.eventId === e.id));
      // if no invitations for event, do not render event
      if (!hasEventInvitation) return null;

      return (
        <div key={e.id} className={styles.responseSection}>
          <h4 className={styles.sectionName}>
            {e.name}
            <EditIcon
              onClick={(): void => {
                goToEdit();
                setEditForm({ type: 'EVENT', id: e.id });
              }}
            />
          </h4>
          {household?.people?.map(p => {
            if (!p.invitations || !p.invitations.length) return null;
            return <InvitationAnswers key={p.id} event={e} guest={p} />;
          })}
        </div>
      );
    });
  }, [events, goToEdit, household, setEditForm]);

  const eventsRendered = renderEvents();
  // check if there are events and if any events have invitations
  const hasInvitation = eventsRendered && !!eventsRendered.length && !!eventsRendered.filter(r => !!r).length;

  return (
    <>
      {!hasInvitation ? (
        <ZeroState />
      ) : (
        <div className={styles.guestResponses}>
          {eventsRendered}
          {renderGeneral()}
        </div>
      )}
    </>
  );
};

export default GuestResponses;
